'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Upload, User, MapPin, FileText } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import StatusBadge from '@/components/ui/StatusBadge';
import { BurialApplication } from './BurialManagementContent';

interface Props {
  application: BurialApplication;
  onClose: () => void;
}

export default function BurialApprovalModal({ application: app, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleApprove = async () => {
    setLoading(true);
    setAction('approve');
    // TODO: BACKEND — POST /api/burial-applications/{app.id}/approve
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    toast.success(`Application ${app.appId} approved — burial scheduled for ${app.scheduledDate}`);
    onClose();
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setLoading(true);
    setAction('reject');
    // TODO: BACKEND — POST /api/burial-applications/{app.id}/reject
    await new Promise((r) => setTimeout(r, 1000));
    setLoading(false);
    toast.error(`Application ${app.appId} rejected`);
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Burial Application — ${app.appId}`}
      subtitle={`Submitted ${app.submittedDate} · Applicant: ${app.applicant}`}
      size="xl"
    >
      <div className="space-y-6">
        {/* Status Row */}
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={app.status} />
          <span className={`text-sm font-600 ${app.permitStatus === 'Uploaded' ? 'text-positive' : 'text-negative'}`}>
            Permit: {app.permitStatus}
          </span>
          {app.status === 'pending' && (
            <span className="text-xs bg-warning/10 text-warning px-2 py-0.5 rounded-full font-600">
              Awaiting Officer Action
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Deceased Details */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <User size={15} className="text-primary" />
              <h3 className="text-sm font-700 text-foreground">Deceased Details</h3>
            </div>
            {[
              ['Full Name', app.deceased],
              ['ID Number', app.idNumber],
              ['Date of Death', app.dateOfDeath],
              ['Applicant', app.applicant],
              ['Relationship', app.relationship],
            ].map(([label, value]) => (
              <div key={`dec-${label}`} className="flex justify-between items-start py-1.5 border-b border-border/50">
                <span className="text-xs text-muted-foreground">{label}</span>
                <span className={`text-xs font-600 text-right ${label === 'ID Number' ? 'font-mono-data' : ''}`}>
                  {value}
                </span>
              </div>
            ))}
          </div>

          {/* Burial Details */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={15} className="text-primary" />
              <h3 className="text-sm font-700 text-foreground">Burial Details</h3>
            </div>
            {[
              ['Cemetery', app.cemetery],
              ['Block', app.block],
              ['Plot', app.plot],
              ['Plot Status', app.plotStatus],
              ['Scheduled Date', app.scheduledDate],
              ['Assigned Officer', app.officer],
            ].map(([label, value]) => (
              <div key={`bur-${label}`} className="flex justify-between items-start py-1.5 border-b border-border/50">
                <span className="text-xs text-muted-foreground">{label}</span>
                <span className={`text-xs font-600 text-right ${label === 'Plot' ? 'font-mono-data' : ''}`}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Documents */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <FileText size={15} className="text-primary" />
            <h3 className="text-sm font-700 text-foreground">Documents</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { id: 'doc-death-cert', label: 'Death Certificate', status: 'Uploaded' },
              { id: 'doc-burial-permit', label: 'Burial Permit', status: app.permitStatus },
              { id: 'doc-id-copy', label: 'ID Copy (Deceased)', status: 'Uploaded' },
              { id: 'doc-id-applicant', label: 'ID Copy (Applicant)', status: 'Uploaded' },
            ].map((doc) => (
              <div key={doc.id} className="border border-border rounded-lg p-3 text-center">
                <div className={`w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center ${
                  doc.status === 'Uploaded' ? 'bg-positive-bg' : doc.status === 'Missing' ? 'bg-negative-bg' : 'bg-warning-bg'
                }`}>
                  <Upload size={14} className={doc.status === 'Uploaded' ? 'text-positive' : doc.status === 'Missing' ? 'text-negative' : 'text-warning'} />
                </div>
                <p className="text-[11px] font-600 text-foreground leading-tight">{doc.label}</p>
                <p className={`text-[11px] mt-0.5 font-600 ${doc.status === 'Uploaded' ? 'text-positive' : doc.status === 'Missing' ? 'text-negative' : 'text-warning'}`}>
                  {doc.status}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Rejection reason */}
        {app.status !== 'completed' && app.status !== 'rejected' && (
          <div>
            <label className="label-base">Rejection Reason (required if rejecting)</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={2}
              placeholder="State the reason for rejecting this application..."
              className="input-base resize-none text-sm"
            />
          </div>
        )}

        {/* Action Buttons */}
        {app.status !== 'completed' && app.status !== 'rejected' && (
          <div className="flex items-center gap-3 pt-2 border-t border-border">
            <button
              onClick={handleApprove}
              disabled={loading}
              className="btn-primary flex-1 justify-center"
            >
              {loading && action === 'approve' ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <CheckCircle size={15} />
              )}
              Approve Application
            </button>
            <button
              onClick={handleReject}
              disabled={loading}
              className="btn-danger flex-1 justify-center"
            >
              {loading && action === 'reject' ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <XCircle size={15} />
              )}
              Reject Application
            </button>
            <button onClick={onClose} className="btn-ghost">
              Cancel
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}